import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, MapPin, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { CompanyCard } from "../components/CompanyCard";

export default function CityDirectory() {
  const { citySlug } = useParams();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cityName, setCityName] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/companies", { params: { city_slug: citySlug, limit: 100 } }).then((r) => {
      setCompanies(r.data?.items || []);
      if (r.data?.items?.length) setCityName(r.data.items[0].city);
      setLoading(false);
    });
    const niceName = citySlug.charAt(0).toUpperCase() + citySlug.slice(1);
    document.title = `Déménageur ${niceName} — Déménageur Pro`;
  }, [citySlug]);

  const displayCity = cityName || (citySlug.charAt(0).toUpperCase() + citySlug.slice(1));

  return (
    <div className="animate-fade-up">
      <section className="bg-gradient-to-b from-[#F7F9FB] to-white py-16 md:py-20">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="text-xs text-[#6B7280] mb-5 flex items-center gap-2" data-testid="breadcrumb">
            <Link to="/" className="hover:text-[#2BD391]">Accueil</Link>
            <span>/</span>
            <Link to="/villes" className="hover:text-[#2BD391]">Villes</Link>
            <span>/</span>
            <span className="text-[#2C3E50] font-semibold">{displayCity}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F0F2F5] text-[#5C6B7A] text-xs font-semibold mb-4 border border-[#E5E7EB]">
                <ShieldCheck className="w-3.5 h-3.5" /> Annuaire des entreprises
              </div>
              <h1 className="font-heading font-extrabold text-4xl md:text-6xl tracking-tight text-[#2C3E50] mb-4 leading-tight">
                Déménageurs<br/>à <span className="text-[#2BD391]">{displayCity}</span>
              </h1>
              <p className="text-[#6B7280] max-w-2xl text-lg">
                Découvrez les <b className="text-[#2C3E50]">entreprises de déménagement à {displayCity}</b>.
                Certaines sont partenaires de Déménageur Pro, d'autres sont référencées à titre informatif.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link to="/devis" className="btn-primary btn-large" data-testid="city-cta-devis">
                Recevoir des devis <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="pt-8 pb-4">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="bg-[#F7F9FB] border border-[#E5E7EB] rounded-2xl p-5 text-xs text-[#6B7280] leading-relaxed" data-testid="directory-notice">
            <p>
              <strong className="text-[#2C3E50]">Comment lire cet annuaire ?</strong>{" "}
              Déménageur Pro référence des entreprises de déménagement à partir d'informations
              professionnelles publiques. Certaines entreprises sont partenaires de la
              plateforme (badge <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#2BD391] text-white rounded-full text-[9px] font-bold">Partenaire</span>),
              d'autres sont simplement listées à titre informatif (badge <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white text-[#5C6B7A] border border-[#E5E7EB] rounded-full text-[9px] font-bold">Référencée</span>).
              Les demandes de devis ne sont transmises qu'aux entreprises partenaires ou
              expressément autorisées à les recevoir. Toute entreprise peut{" "}
              <Link to="/fiche-entreprise/modifier" className="text-[#1AA66C] font-semibold hover:underline">
                demander la modification ou la suppression de sa fiche
              </Link>.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container mx-auto px-6 lg:px-10">
          {loading ? (
            <div className="text-center text-sm text-[#6B7280] py-20" data-testid="city-loading">Chargement…</div>
          ) : companies.length === 0 ? (
            <div className="card-clean text-center max-w-xl mx-auto" data-testid="city-empty">
              <div className="w-16 h-16 bg-[#E8FBF3] rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-[#2BD391]" />
              </div>
              <h2 className="font-heading font-extrabold text-2xl mb-2 text-[#2C3E50]">Aucune entreprise pour {displayCity}</h2>
              <p className="text-[#6B7280] mb-6">Faites votre demande de devis, nous trouverons les meilleurs déménageurs de votre région.</p>
              <Link to="/devis" className="btn-primary">Demander un devis</Link>
            </div>
          ) : (
            <>
              <div className="mb-8 text-sm text-[#6B7280]" data-testid="city-count">
                <b className="text-[#2C3E50]">{companies.length}</b> entreprise{companies.length > 1 ? "s" : ""} trouvée{companies.length > 1 ? "s" : ""}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {companies.map((c) => <CompanyCard key={c.id} company={c} />)}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
